import { useState } from "react";
import {
  ButtonAdd,
  ButtonDelete,
} from "@/components/ui/button";
import MenuFormModal from "@/components/menu/MenuFormModal";
import Switch from "@/components/ui/switch";
import { useModal, usePagination } from "@/lib/hooks";
import { useDelete } from "@/lib/useDelete";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useChangeMenuStatusMutation,
  useDeleteMenuMutation,
  useGetMenusQuery,
} from "@/redux/services/menu/menu.api";
import { Menu, MenuStatusFormData } from "@/redux/services/menu/menu.type";
import toast from "react-hot-toast";
import { BookOpen, Layers, UtensilsCrossed } from "lucide-react";
import { Link } from "react-router-dom";
import { ButtonEdit } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import ReactPagination from "@/components/Table/Pagination";

function Delete({ item }: { item: Menu }) {
  const [deleteItem, { isSuccess, isError, error }] = useDeleteMenuMutation();
  const onDelete = useDelete<Menu>({
    item,
    deleteItem,
    isSuccess,
    isError,
    error,
    successMessage: "Menu supprimé",
  });
  return <ButtonDelete onClick={onDelete} />;
}

const TabsMenu = () => {
  const { limit, onChange, page } = usePagination(12);
  const { currentRestaurant, currentUser } = useAppSelector(
    (state) => state.user
  );
  const { canManage } = usePermissions();
  // Owners stay read-only on menus (existing product behaviour); other staff
  // need the menu.manage permission to make changes.
  const isReadOnly =
    currentUser?.roles?.includes(Roles.owner) || !canManage("menu");
  const { data, isLoading } = useGetMenusQuery({
    limit,
    page,
    restaurantId: currentRestaurant?.id,
  });
  const [changeMenuStatus] = useChangeMenuStatusMutation();
  const [loadingMenus, setLoadingMenus] = useState<Record<string, boolean>>({});
  const {
    isOpen,
    openModal,
    openEditModal,
    closeModal,
    item: selectedMenu,
  } = useModal<Menu>();

  const handleStatusChange = async (menu: Menu) => {
    setLoadingMenus((prev) => ({ ...prev, [menu.id]: true }));
    try {
      const formData: MenuStatusFormData = {
        status: menu.status === "active" ? "inactive" : "active",
        deactivateOtherMenus: menu.status !== "active",
      };
      const res = await changeMenuStatus({ id: menu.id, data: formData });
      if ("data" in res) {
        toast.success(
          `Menu ${res.data?.status === "active" ? "activé" : "désactivé"} avec succès !`
        );
      } else {
        toast.error("Une erreur s'est produite !");
      }
    } finally {
      setLoadingMenus((prev) => ({ ...prev, [menu.id]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between">
          <Skeleton className="w-40 h-9" />
          <Skeleton className="w-44 h-9" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const totalItems = (menu: Menu) =>
    menu.menuSections?.reduce(
      (sum, s) => sum + (s.menuItems?.length ?? 0),
      0
    ) ?? 0;

  return (
    <>
      <div className="flex justify-end flex-wrap gap-4 mb-6">
        {!isReadOnly && (
          <ButtonAdd
            label="Créer un nouveau menu"
            onClick={() => openModal()}
          />
        )}
      </div>

      {data?.results && data.results.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.results.map((menu) => {
            const sections = menu.menuSections ?? [];
            const itemCount = totalItems(menu);
            const isActive = menu.status === "active";

            return (
              <div
                key={menu.id}
                className="bg-card rounded-lg border border-border shadow-sm hover:shadow-md transition-shadow flex flex-col"
              >
                {/* Card header */}
                <div className="p-4 flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <Link
                      to={`/menus/${menu.id}/`}
                      state={menu}
                      className="flex-1 min-w-0"
                    >
                      <h3 className="font-semibold text-card-foreground text-base hover:text-primary transition-colors">
                        {menu.title}
                      </h3>
                      {menu.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {menu.description}
                        </p>
                      )}
                    </Link>
                    <span
                      className={`flex-shrink-0 ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                        isActive
                          ? "bg-accent-light text-accent"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {isActive ? "Actif" : "Inactif"}
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                    <span className="flex items-center gap-1.5">
                      <Layers className="h-3.5 w-3.5" />
                      {sections.length} section
                      {sections.length !== 1 ? "s" : ""}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <UtensilsCrossed className="h-3.5 w-3.5" />
                      {itemCount} article{itemCount !== 1 ? "s" : ""}
                    </span>
                  </div>

                  {/* Section names as pills */}
                  {sections.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {sections.slice(0, 4).map((section) => (
                        <span
                          key={section.id}
                          className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted text-xs font-medium text-muted-foreground"
                        >
                          {section.title}
                        </span>
                      ))}
                      {sections.length > 4 && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted text-xs font-medium text-muted-foreground">
                          +{sections.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Card footer */}
                <div className="px-4 py-3 border-t border-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`menu-status-${menu.id}`}
                      checked={isActive}
                      onChange={() => handleStatusChange(menu)}
                      disabled={loadingMenus[menu.id]}
                    />
                    <span className="text-xs text-muted-foreground">
                      {loadingMenus[menu.id] ? "Mise à jour..." : isActive ? "Activé" : "Désactivé"}
                    </span>
                  </div>
                  {!isReadOnly && (
                    <div className="flex items-center gap-1">
                      <ButtonEdit onClick={() => openEditModal(menu)} />
                      <Delete item={menu} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12">
          <BookOpen className="h-12 w-12 text-muted-foreground opacity-30 mb-3" />
          <p className="text-muted-foreground text-lg mb-2">Aucun menu</p>
          <p className="text-muted-foreground text-sm">
            Cliquez sur "Créer un nouveau menu" pour commencer
          </p>
        </div>
      )}

      {data?.results &&
        data.results.length > 0 &&
        data?.count &&
        (data.count > limit || limit > 12) && (
          <div className="flex-shrink-0 mt-10">
            <ReactPagination
              itemsPerPage={limit}
              count={data.count}
              onChange={onChange}
              currentPage={page}
              options={[
                { value: 12, label: "12" },
                { value: 24, label: "24" },
              ]}
            />
          </div>
        )}

      <MenuFormModal isOpen={isOpen} onClose={closeModal} menu={selectedMenu} />
    </>
  );
};

export default TabsMenu;
